import React from "react";
import { TimeService, useTime } from "@me8eon/time";
import { makeContextFor } from "@me8eon/context";
import { useSessionId } from "@me8eon/session";
import { useThrowError } from "@me8eon/throw_error";
import { useJwtToken } from "@me8eon/react_authentication";
import { JwtTokenFn } from "@me8eon/authentication";

export type LikeOrDislike = "like" | "dislike";

export const extractNewAiSource = (userAnswer: string): string | undefined => {
    if (!userAnswer) return undefined;
    const agentFromAnswer = userAnswer.split("**Source**: ")[1]?.trim()
    return agentFromAnswer ? agentFromAnswer : "Guided Agent";
}

// Helper function to extract source from answer string
export const extractSourceFromAnswer = (answer: string): string | undefined => {
    if (!answer) return undefined;

    // Pattern to match: <b> Source: {source name} </b>
    // Handle both <b> and \u003cb\u003e (escaped HTML)
    // Also handle </b> and \u003c/b\u003e

    const patterns = [
        /\u003cb\u003e\s*Source:\s*([^\u003c]+)\u003c\/b\u003e/i,
        /<b>\s*Source:\s*([^<]+)<\/b>/i,
        /\*\*Source\*\*:\s*([^\n]+)/i,
        /\*\*Source:\s*([^*]+)\*\*/i,
        /Source:\s*([^\n]+)/i
    ];

    for (const pattern of patterns) {
        const match = answer.match(pattern);
        if (match && match[1]) {
            return match[1].trim();
        }
    }

    return undefined;
};

export type QueryFeedbackPayload = {
    source: "query";
    page: "simple" | "advanced" | "all-apps";
    searchType?: string
    query: string;
    role: string;
};

export type incidentFeedbackPayload = {
    source: "incident";
    incNumber: string;
    classification: any;
    text: string;
    query: string;
    answer?: string;
    sourceFromAnswer?: string;
    likeDislike?: LikeOrDislike;
    like?: LikeOrDislike,
    id?: string
    company: string;
    role: string;
    short_description: string;
};

export type ClickFeedbackPayload = {
    source: "click";
    href: string;
    index: string;
    id: string;
    position?: number;
    query?: string;
    sources?: any[];
    sourceFromAnswer?: string;
};
export type SearchFeedbackPayload = {
    source: "search";
    answer?: string;
    query: string;
    index: string;
    id: string;
    like: LikeOrDislike | undefined;
    company: string;
    role: string;
};
export type ChatbotQueryFeedbackPayload = {
    source: "chatquery";
    query: string;
};
export type AiSource = {
    index: string
    id: string
    summary?: string
}
export type AiAssistSubmitInput = {
    source: "aiassist-query";
    messages: any[];
    query: string;
    company: string;
    role: string;
};
export type RequestCallbackFeedbackPayload = {
    source: "request-callback";
    messages: any[];
    query: string;
    company: string;
    role: string;
    short_description: string;
};
export type LiveChatFeedbackPayload = {
    source: "live-chat";
    messages: any[];
    query: string;
    company: string;
    role: string;
    short_description: string;
};
export type TeamsAgentFeedbackPayload = {
    source: "teams-agent";
    messages: any[];
    query: string;
    company: string;
    role: string;
    meetingId: string;
    meetingDate: string;
    meetingStartTime: string;
    meetingEndTime: string;
};
export type AiAssistFeedbackPayload = {
    source: "ai-assist" | "aiassist-query";
    classification: any;
    messages: any[];
    query: string;
    like?: LikeOrDislike;
    sources: AiSource[]
    answer?: string;
    role: string;
    sourceFromAnswer?: string;
    company: string;
};
export type FreeTextPayload = {
    source: "freeTextFromChat";
    classification: any;
    text: string;
    query: string;
    answer?: string;
    likeDislike?: LikeOrDislike;
    like?: LikeOrDislike,
    id?: string
    sourceFromAnswer?: string;
    company: string;
    role: string;
};

export type FeedbackPayload =
    AiAssistSubmitInput
    | SearchFeedbackPayload
    | AiAssistFeedbackPayload
    | QueryFeedbackPayload
    | ClickFeedbackPayload
    | ChatbotQueryFeedbackPayload
    | FreeTextPayload
    | incidentFeedbackPayload
    | RequestCallbackFeedbackPayload
    | LiveChatFeedbackPayload
    | TeamsAgentFeedbackPayload;

export type FeedbackFn = (p: FeedbackPayload) => Promise<void>;
export const nullFeedbackFn: FeedbackFn = async () => { };
export type RawFeedbackFn = (
    sessionId: string,
    accessToken: JwtTokenFn,
    timeService: TimeService,
) => FeedbackFn;

export const { Provider: FeedbackProvider, context: FeedbackContext } = makeContextFor<RawFeedbackFn, "feedbackFn">("feedbackFn");

export const useFeedback = (): FeedbackFn => {
    const sessionId = useSessionId();
    const time = useTime();
    const throwError = useThrowError();
    const feedbackCreator = React.useContext(FeedbackContext);
    if (feedbackCreator === undefined) return throwError("s/w", "useFeedback needs a feedbackCreator provider");
    const accessToken = useJwtToken();
    return feedbackCreator(sessionId, accessToken, time);
};

export type FeedbackProps = {
    dataToSend: (likeDislike: LikeOrDislike | undefined) => Promise<FeedbackPayload>;
    aiChatbotIndex?: number;
    createIncidentHandler?: (reason: string) => void;
    showCreateIncident?: boolean;
    source: string;
};

export type DisplayFeedbackComponent = (props: FeedbackProps) => React.ReactElement;
export const { use: useDisplayFeedbackComponent, Provider: DisplayFeedbackComponentProvider } = makeContextFor<DisplayFeedbackComponent, "displayFeedbackComponent">("displayFeedbackComponent");